import { useEffect, useRef } from 'react'

export type ParticleTarget =
  | { kind: 'text'; text: string }
  | { kind: 'shape'; draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void }
  | { kind: 'scatter' }

type ParticleFieldProps = {
  targets: ParticleTarget[]
  /** Which target the particles form now. */
  index: number
  /** Hold the current formation without animating. */
  still?: boolean
  /** Horizontal band the formations occupy, as fractions of the width. */
  areaStart?: number
  areaWidth?: number
  className?: string
}

/** Flag colors for a formed particle: 0 red stripe, 1 white stripe, 2 blue canton. */
type Particle = { x: number; y: number; vx: number; vy: number; tx: number; ty: number; tone: number; on: boolean; flag: number }

/** Reads a role color from the surrounding theme. */
function role(el: Element, name: string, fallback: string) {
  return getComputedStyle(el).getPropertyValue(name).trim() || fallback
}

/**
 * Thousands of particles that gather into a word or a drawing and scatter
 * from the pointer. Each target is drawn once to an offscreen canvas and
 * sampled, so a new formation is a drawing, not an animation by hand.
 */
export default function ParticleField({ targets, index, still = false, className, areaStart = 0, areaWidth = 1 }: ParticleFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const state = useRef({ index, still })

  useEffect(() => {
    state.current = { index, still }
  }, [index, still])

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    let width = 0
    let height = 0
    let scale = 1
    let particles: Particle[] = []
    let samples: Array<Array<[number, number]>> = []
    let formed = -1
    let frame = 0
    let visible = true
    const pointer = { x: -9999, y: -9999 }
    let colors = ['#f5f6fa', '#ff5252', '#66aef5']
    let flagColors = ['#d80007', '#f5f6fa', '#4a7fd6']

    const sample = (target: ParticleTarget) => {
      const off = document.createElement('canvas')
      const offset = Math.round(width * areaStart)
      const bandWidth = Math.max(1, Math.round(width * areaWidth))
      off.width = bandWidth
      off.height = height
      const o = off.getContext('2d')!
      o.fillStyle = '#000'
      o.strokeStyle = '#000'
      if (target.kind === 'scatter') return []
      if (target.kind === 'text') {
        let size = height * 0.42
        o.font = `400 ${size}px "Instrument Serif", Georgia, serif`
        const measured = o.measureText(target.text).width
        if (measured > bandWidth * 0.86) size *= (bandWidth * 0.86) / measured
        o.font = `400 ${size}px "Instrument Serif", Georgia, serif`
        o.textAlign = 'center'
        o.textBaseline = 'middle'
        o.fillText(target.text, bandWidth / 2, height / 2)
      } else target.draw(o, bandWidth, height)
      const data = o.getImageData(0, 0, bandWidth, height).data
      const step = Math.max(3, Math.round(Math.sqrt((bandWidth * height) / 9000)))
      const points: Array<[number, number]> = []
      for (let y = 0; y < height; y += step)
        for (let x = 0; x < bandWidth; x += step)
          if (data[(y * bandWidth + x) * 4 + 3] > 128) points.push([x + offset, y])
      for (let i = points.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[points[i], points[j]] = [points[j], points[i]]
      }
      return points
    }

    const resize = () => {
      scale = Math.min(window.devicePixelRatio || 1, 2)
      width = Math.max(1, Math.round(canvas.clientWidth))
      height = Math.max(1, Math.round(canvas.clientHeight))
      canvas.width = width * scale
      canvas.height = height * scale
      ctx.setTransform(scale, 0, 0, scale, 0, 0)
      samples = targets.map(sample)
      const count = Math.min(2600, Math.max(600, Math.round((width * height) / 380)))
      particles = Array.from({ length: count }, (_, i) => {
        const prior = particles[i]
        return {
          x: prior?.x ?? Math.random() * width,
          y: prior?.y ?? Math.random() * height,
          vx: 0,
          vy: 0,
          tx: 0,
          ty: 0,
          tone: Math.random() < 0.08 ? 1 : Math.random() < 0.1 ? 2 : 0,
          on: false,
          flag: 1,
        }
      })
      formed = -1
      colors = [
        role(canvas, '--mds-ink', '#f5f6fa'),
        role(canvas, '--mds-accent', '#ff5252'),
        role(canvas, '--mds-link', '#66aef5'),
      ]
      flagColors = [role(canvas, '--mds-flag-red', '#d80007'), '#f5f6fa', role(canvas, '--mds-flag-blue', '#4a7fd6')]
    }

    // A formed word wears the flag: a blue canton at the upper left and
    // thirteen red and white stripes across the rest of its bounds.
    const form = (target: number) => {
      const points = samples[target] ?? []
      let [left, top, right, bottom] = [Infinity, Infinity, -Infinity, -Infinity]
      for (const [x, y] of points) {
        left = Math.min(left, x)
        right = Math.max(right, x)
        top = Math.min(top, y)
        bottom = Math.max(bottom, y)
      }
      particles.forEach((p, i) => {
        if (i < points.length) {
          p.tx = points[i][0]
          p.ty = points[i][1]
          p.on = true
          const u = (p.tx - left) / Math.max(1, right - left)
          const v = (p.ty - top) / Math.max(1, bottom - top)
          p.flag = u < 0.4 && v < 7 / 13 ? 2 : Math.min(12, Math.floor(v * 13)) % 2
        } else {
          p.tx = Math.random() * width
          p.ty = Math.random() * height
          p.on = false
        }
      })
      formed = target
    }

    const draw = () => {
      frame = 0
      const { index: target, still: hold } = state.current
      if (target !== formed) form(target)
      ctx.clearRect(0, 0, width, height)
      for (const p of particles) {
        if (hold) {
          p.x = p.tx
          p.y = p.ty
        } else {
          const dx = p.x - pointer.x
          const dy = p.y - pointer.y
          const d2 = dx * dx + dy * dy
          if (d2 < 9000) {
            const push = (9000 - d2) / 9000
            p.vx += (dx / Math.sqrt(d2 + 1)) * push * 2.4
            p.vy += (dy / Math.sqrt(d2 + 1)) * push * 2.4
          }
          p.vx += (p.tx - p.x) * 0.012
          p.vy += (p.ty - p.y) * 0.012
          p.vx *= 0.86
          p.vy *= 0.86
          p.x += p.vx
          p.y += p.vy
        }
        ctx.globalAlpha = p.on ? 0.92 : 0.16
        ctx.fillStyle = p.on ? flagColors[p.flag] : colors[p.tone]
        ctx.fillRect(p.x, p.y, p.on ? 1.8 : 1.2, p.on ? 1.8 : 1.2)
      }
      ctx.globalAlpha = 1
      if (visible && !hold) frame = requestAnimationFrame(draw)
    }

    const kick = () => {
      if (!frame) frame = requestAnimationFrame(draw)
    }

    const onMove = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      pointer.x = event.clientX - rect.left
      pointer.y = event.clientY - rect.top
    }
    const onLeave = () => {
      pointer.x = -9999
      pointer.y = -9999
    }

    const sizes = new ResizeObserver(() => {
      resize()
      kick()
    })
    sizes.observe(canvas)
    const seen = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      if (visible) kick()
    })
    seen.observe(canvas)
    const parent = canvas.parentElement
    parent?.addEventListener('pointermove', onMove)
    parent?.addEventListener('pointerleave', onLeave)
    // Formations re-sample once the display font has loaded.
    void document.fonts?.ready.then(() => {
      resize()
      kick()
    })
    const poll = window.setInterval(kick, 250)

    return () => {
      cancelAnimationFrame(frame)
      window.clearInterval(poll)
      sizes.disconnect()
      seen.disconnect()
      parent?.removeEventListener('pointermove', onMove)
      parent?.removeEventListener('pointerleave', onLeave)
    }
  }, [targets, areaStart, areaWidth])

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />
}
